import { KeyRound, Loader2, Plus, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { oauthClientService } from "@/lib/services";
import type { OAuthClient, OAuthClientCreate, OAuthClientUpdate } from "@/types";
import { CreateOAuthClientModal } from "./oauth-clients/CreateOAuthClientModal";
import { EditOAuthClientModal } from "./oauth-clients/EditOAuthClientModal";
import { OAuthClientList } from "./oauth-clients/OAuthClientList";
import { OAuthClientSecretModal } from "./oauth-clients/OAuthClientSecretModal";

export function AdminOAuthClients() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [clients, setClients] = useState<OAuthClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<OAuthClient | null>(null);
  const [saving, setSaving] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);

  const isInstallAdmin = user?.role === "install_admin";

  const load = async () => {
    setLoading(true);
    try {
      const res = await oauthClientService.list();
      setClients(res.data);
    } catch {
      showToast("Failed to load OAuth clients", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isInstallAdmin) void load();
    else setLoading(false);
  }, [isInstallAdmin]);

  if (!isInstallAdmin) {
    return (
      <div className="flex items-center justify-center p-10 text-center">
        <div>
          <Shield className="h-8 w-8 text-red-400 mx-auto mb-3" />
          <p className="text-content-tertiary">OAuth client management is restricted to the install admin.</p>
        </div>
      </div>
    );
  }

  const handleCreate = async (payload: OAuthClientCreate) => {
    setSaving(true);
    try {
      const res = await oauthClientService.create(payload);
      setShowCreate(false);
      // Confidential clients return the secret once — surface it prominently.
      setNewSecret(res.data.clientSecret);
      showToast("Client created", "success");
      await load();
    } catch {
      showToast("Failed to create client", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (client: OAuthClient) => {
    try {
      await oauthClientService.update(client.uuid, { isEnabled: !client.isEnabled });
      await load();
    } catch {
      showToast("Failed to update client", "error");
    }
  };

  const handleUpdate = async (uuid: string, payload: OAuthClientUpdate) => {
    setSaving(true);
    try {
      await oauthClientService.update(uuid, payload);
      setEditing(null);
      showToast("Client updated", "success");
      await load();
    } catch {
      showToast("Failed to update client", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client: OAuthClient) => {
    if (!confirm(`Delete OAuth client "${client.clientName}"? This cannot be undone.`)) return;
    try {
      await oauthClientService.remove(client.uuid);
      showToast("Client deleted", "success");
      await load();
    } catch {
      showToast("Failed to delete client (first-party clients can't be deleted)", "error");
    }
  };

  const copy = (value: string) => {
    void navigator.clipboard.writeText(value);
    showToast("Copied", "success");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-content-primary flex items-center gap-2">
            <KeyRound className="h-5 w-5" /> OAuth Clients
          </h2>
          <p className="text-sm text-content-tertiary mt-1">
            Applications allowed to authenticate through the Anthm OAuth/OIDC issuer.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-md bg-brand-primary text-white text-sm font-medium hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New Client
        </button>
      </div>

      {loading && (
        <div className="flex justify-center p-10">
          <Loader2 className="h-6 w-6 animate-spin text-content-tertiary" />
        </div>
      )}

      {!loading && clients.length === 0 && (
        <p className="text-content-tertiary p-6 text-center">No OAuth clients yet.</p>
      )}

      {!loading && clients.length > 0 && (
        <OAuthClientList
          clients={clients}
          onToggleEnabled={toggleEnabled}
          onEdit={setEditing}
          onDelete={handleDelete}
          onCopy={copy}
        />
      )}

      {newSecret !== null && (
        <OAuthClientSecretModal secret={newSecret} onClose={() => setNewSecret(null)} onCopy={copy} />
      )}

      {showCreate && (
        <CreateOAuthClientModal
          saving={saving}
          onClose={() => setShowCreate(false)}
          onValidationError={(message) => showToast(message, "error")}
          onCreate={handleCreate}
        />
      )}

      {editing !== null && (
        <EditOAuthClientModal
          key={editing.uuid}
          client={editing}
          saving={saving}
          onClose={() => setEditing(null)}
          onValidationError={(message) => showToast(message, "error")}
          onSave={handleUpdate}
        />
      )}
    </div>
  );
}
